import {
  LOGISTICS_ROUTE_COORDINATE_DECIMALS,
  LOGISTICS_ROUTE_MAX_WAYPOINTS,
  type LogisticsRoute,
  type LogisticsVehicleType,
  roundRouteCoordinate,
} from '@gozoki/api-types'
import type { RawRequestArgs } from '@tuyau/core/types'
import type { routes } from 'gozoki-backend/registry'

/**
 * Tracé routier réel d'un parcours ordonné sur une carte (`POST /logistics/routing/route`) : la
 * création de tournée (un parcours en cours d'édition, `useLogisticsRouteTrace`) et les cartes des
 * tournées du simulateur (les tracés d'un scénario, `RouteTraceLayers`) ; la fiche hub (atelier →
 * hub, `HubLocationMap`, sur le même hook que la création). Jamais de trait droit à la place de la
 * route (décision du 14/10, LU-121) : sans tracé, la carte ne dessine rien. Seule exception, les
 * mini-cartes de la modale des alternatives (26/10) : sans tracé, elles gardent les lignes droites
 * entre les arrêts (`routeTraceLinesOrStraightOf`), qui en montrent au moins l'ordre.
 */

/**
 * Attente après la dernière modification d'un parcours en cours d'édition, avant de le tracer :
 * plus longue que l'aperçu de charge (400 ms), chaque tracé hors cache se facture.
 */
export const ROUTE_TRACE_EDIT_DEBOUNCE_MS = 800

export type RouteTraceBody = RawRequestArgs<(typeof routes)['logistics.routing.route']>['body']

/**
 * Clé TanStack Query d'un tracé, la même pour toutes les cartes : un corps déjà tracé ailleurs ne
 * se redemande pas. La création de tournée et les cartes du simulateur ne la partagent que pour un
 * même corps (mêmes étapes, même gabarit), ce qui reste rare : la première trace un seul parcours
 * atelier → boutiques, les secondes un appel par segment du scénario. `key` : le corps sérialisé
 * (`routeTraceKeyOf`), `null` tant qu'il n'y a rien à tracer.
 */
export const routeTraceQueryKey = (key: string | null) => ['logistics', 'routing', 'route', key]

/** Corps sérialisé, dernier élément de `routeTraceQueryKey`. */
export const routeTraceKeyOf = (body: RouteTraceBody): string => JSON.stringify(body)

export interface RouteTracePoint {
  latitude: number
  longitude: number
}

/** Pourquoi une carte n'appelle pas le routeur : moins de deux étapes situées, ou trop d'étapes. */
export type RouteTraceSkipReason = 'not_enough_waypoints' | 'too_many_waypoints'

/**
 * Corps de la requête, ou la raison de ne pas l'envoyer : au-delà de
 * `LOGISTICS_ROUTE_MAX_WAYPOINTS`, la route refuse le parcours, et un tracé tronqué tromperait.
 */
export type RouteTraceRequest =
  | { ok: true; body: RouteTraceBody }
  | { ok: false; reason: RouteTraceSkipReason }

export const routeTraceBodyOf = (
  points: readonly RouteTracePoint[],
  vehicleType: LogisticsVehicleType,
): RouteTraceRequest => {
  if (points.length < 2) return { ok: false, reason: 'not_enough_waypoints' }
  if (points.length > LOGISTICS_ROUTE_MAX_WAYPOINTS) {
    return { ok: false, reason: 'too_many_waypoints' }
  }
  return { ok: true, body: routeTraceBodyFrom(points, vehicleType) }
}

/**
 * Seule construction du corps : deux cartes qui tracent les mêmes étapes en tirent la même clé.
 * Étapes arrondies comme le serveur (`roundRouteCoordinate`) : deux saisies qui ne diffèrent
 * qu'au-delà font une seule clé, donc un seul appel au routeur.
 */
const routeTraceBodyFrom = (
  points: readonly RouteTracePoint[],
  vehicleType: LogisticsVehicleType,
): RouteTraceBody => ({
  vehicleType,
  waypoints: points.map(({ latitude, longitude }) => ({
    latitude: roundRouteCoordinate(latitude),
    longitude: roundRouteCoordinate(longitude),
  })),
})

export interface RouteTraceLines {
  /** Tronçons praticables par le gabarit, en `[longitude, latitude]`. */
  lines: [number, number][][]
  /** Tronçons qui empruntent une route interdite ou restreinte au gabarit (PTV `violated`). */
  restrictedLines: [number, number][][]
}

export const NO_ROUTE_TRACE_LINES: RouteTraceLines = { lines: [], restrictedLines: [] }

/** Lignes d'un tracé, une par tronçon, rangées selon qu'il est praticable ou non. */
export const routeTraceLinesOf = (route: LogisticsRoute | undefined): RouteTraceLines => {
  if (route?.status !== 'ok') return NO_ROUTE_TRACE_LINES
  const lines: [number, number][][] = []
  const restrictedLines: [number, number][][] = []
  for (const leg of route.legs) {
    if (leg.restricted) restrictedLines.push(leg.geometry.coordinates)
    else lines.push(leg.geometry.coordinates)
  }
  return { lines, restrictedLines }
}

/** Lignes de plusieurs tracés réunies, dans l'ordre des tracés (cf. `routeTraceLinesOf`). */
export const routeTraceLinesOfAll = (
  routes: readonly (LogisticsRoute | undefined)[],
): RouteTraceLines => {
  const traced = routes.map(routeTraceLinesOf)
  return {
    lines: traced.flatMap((trace) => trace.lines),
    restrictedLines: traced.flatMap((trace) => trace.restrictedLines),
  }
}

/** Tracés d'un ensemble d'appels (`RouteTraceLayers`), avec l'état de ces appels. */
export interface RouteTraces extends RouteTraceLines {
  /** Un appel est en cours, ou relu après une panne du fournisseur. */
  pending: boolean
  /** Au moins un appel, et tous ont rendu un tracé (`ok`). */
  complete: boolean
}

/** Tracés de plusieurs appels, dans l'ordre des appels, et leur état (cf. `RouteTraces`). */
export const routeTracesOf = (
  results: readonly {
    data: LogisticsRoute | undefined
    isPending: boolean
    isFetching: boolean
  }[],
): RouteTraces => {
  const routes = results.map((result) => result.data)
  return {
    ...routeTraceLinesOfAll(routes),
    pending: results.some((result) => result.isPending || result.isFetching),
    complete: routes.length > 0 && routes.every((route) => route?.status === 'ok'),
  }
}

/** Lignes d'une mini-carte des alternatives ; `straight` : repli en lignes droites, signalé. */
export interface MiniMapRouteLines extends RouteTraceLines {
  straight: boolean
}

/**
 * Lignes d'une mini-carte des alternatives : la route réelle quand tous ses appels l'ont tracée ;
 * rien pendant qu'un appel est en cours (pas de lignes droites remplacées aussitôt) ; sinon (aucun
 * appel, un camion supprimé notamment, cf. `alternativeRouteBodiesOf`, échec, pas de route ou pas
 * de routeur) les lignes droites entre les arrêts. Un seul appel sans route suffit au repli : pas
 * de route à moitié tracée.
 */
export const routeTraceLinesOrStraightOf = (
  traces: RouteTraces,
  straightLines: [number, number][][],
): MiniMapRouteLines => {
  if (traces.complete) {
    return { lines: traces.lines, restrictedLines: traces.restrictedLines, straight: false }
  }
  if (traces.pending) return { ...NO_ROUTE_TRACE_LINES, straight: false }
  return { lines: straightLines, restrictedLines: [], straight: true }
}

/**
 * - `none` : rien à tracer (moins de deux étapes situées) ;
 * - `too_many_waypoints` : plus d'étapes que la route n'en accepte, aucun appel ;
 * - `pending` : la saisie n'est pas encore posée, ou le tracé est en cours ;
 * - `unavailable` : pas de routeur réel, échec du routeur ou de la requête.
 */
export type RouteTraceStatus = 'none' | 'too_many_waypoints' | 'pending' | 'ok' | 'unavailable'

/**
 * Statut du tracé affiché. `settled` : le tracé du parcours courant est demandé (saisie posée, ou
 * parcours déjà tracé) ; sinon, rien n'est montré, l'ancien tracé ne passe plus par les étapes.
 */
export const routeTraceStatusOf = ({
  request,
  settled,
  trace,
}: {
  request: RouteTraceRequest
  settled: boolean
  trace: {
    isPending: boolean
    isFetching: boolean
    isError: boolean
    data: LogisticsRoute | undefined
  }
}): RouteTraceStatus => {
  if (!request.ok) return request.reason === 'too_many_waypoints' ? 'too_many_waypoints' : 'none'
  // `isFetching` : une panne en cache, relue (`routeTraceStaleTime`), n'est pas montrée pendant
  // qu'on la redemande.
  if (!settled || trace.isPending || trace.isFetching) return 'pending'
  if (trace.isError || trace.data?.status !== 'ok') return 'unavailable'
  return 'ok'
}

/** Tronçon d'un parcours déjà localisé : son véhicule et ses lieux en `[lng, lat]`, dans l'ordre. */
export interface LocatedTrace {
  vehicleType: LogisticsVehicleType
  coordinates: [number, number][]
}

/**
 * Même étape pour le routeur : le serveur arrondit les coordonnées avant l'appel, deux lieux à
 * moins d'un mètre l'un de l'autre y arrivent confondus.
 */
const samePoint = (a: [number, number], b: [number, number]) =>
  a[0].toFixed(LOGISTICS_ROUTE_COORDINATE_DECIMALS) ===
    b[0].toFixed(LOGISTICS_ROUTE_COORDINATE_DECIMALS) &&
  a[1].toFixed(LOGISTICS_ROUTE_COORDINATE_DECIMALS) ===
    b[1].toFixed(LOGISTICS_ROUTE_COORDINATE_DECIMALS)

/** Sans deux étapes consécutives au même endroit : le routeur n'a rien à tracer entre elles. */
const withoutRepeatedPoints = (coordinates: [number, number][]): [number, number][] =>
  coordinates.filter((point, index) => index === 0 || !samePoint(point, coordinates[index - 1]!))

/**
 * Découpe à `LOGISTICS_ROUTE_MAX_WAYPOINTS` étapes, chaque morceau repartant de la dernière étape
 * du précédent : les tracés se raccordent.
 */
const chunked = (coordinates: [number, number][]): [number, number][][] => {
  const step = LOGISTICS_ROUTE_MAX_WAYPOINTS - 1
  const chunks: [number, number][][] = []
  for (let start = 0; start < coordinates.length - 1; start += step) {
    chunks.push(coordinates.slice(start, start + LOGISTICS_ROUTE_MAX_WAYPOINTS))
  }
  return chunks
}

/**
 * Appels au routeur pour les tracés d'une tournée, dans l'ordre de passage. Chaque tracé hors
 * cache se facture chez le fournisseur, une transaction par appel : deux tracés consécutifs du
 * même type de véhicule, le second partant où finit le premier (les tournées d'un hub, qui y
 * reviennent), partent dans un seul appel, découpé au plafond d'étapes de la route.
 *
 * Contrepartie assumée : la réponse vaut pour tout l'appel. Une étape sans route (`no_route`) ou
 * une panne efface les tracés de toutes les tournées fusionnées dans cet appel, pas seulement le
 * sien ; une tournée à cheval sur deux morceaux de la découpe peut n'être tracée qu'en partie.
 */
export const routeTraceBodiesOf = (traces: readonly LocatedTrace[]): RouteTraceBody[] => {
  const merged: LocatedTrace[] = []
  for (const trace of traces) {
    const previous = merged.at(-1)
    const previousEnd = previous?.coordinates.at(-1)
    if (
      previous !== undefined &&
      previousEnd !== undefined &&
      previous.vehicleType === trace.vehicleType &&
      trace.coordinates[0] !== undefined &&
      samePoint(previousEnd, trace.coordinates[0])
    ) {
      previous.coordinates = [...previous.coordinates, ...trace.coordinates.slice(1)]
    } else {
      merged.push({ vehicleType: trace.vehicleType, coordinates: trace.coordinates })
    }
  }

  return merged.flatMap(({ vehicleType, coordinates }) =>
    chunked(withoutRepeatedPoints(coordinates)).map((chunk) =>
      routeTraceBodyFrom(
        chunk.map(([longitude, latitude]) => ({ latitude, longitude })),
        vehicleType,
      ),
    ),
  )
}

/**
 * Appels distincts, dans l'ordre de leur première occurrence : deux tournées au même parcours (un
 * brouillon et sa version validée) n'en font qu'un, tracé une seule fois. Comparés sur la clé de
 * leur requête (`routeTraceKeyOf`).
 */
export const uniqueRouteTraceBodies = (bodies: readonly RouteTraceBody[]): RouteTraceBody[] => {
  const byKey = new Map<string, RouteTraceBody>()
  for (const body of bodies) {
    const key = routeTraceKeyOf(body)
    if (!byKey.has(key)) byKey.set(key, body)
  }
  return [...byKey.values()]
}

/**
 * Durée de fraîcheur d'un tracé reçu. Un tracé ou l'absence de routeur ne
 * changent pas pour un même parcours : jamais relus tant qu'ils restent dans le cache du
 * navigateur (cinq minutes après la dernière carte qui les affiche ; au-delà, le serveur répond
 * sans nouvel appel au fournisseur par les tronçons qu'il garde en base sept jours, LU-166),
 * chaque tracé hors cache se facturant. Une
 * panne du fournisseur (`provider_error` : délai, quota, 5xx) ou un `no_route` de lot est relu au prochain affichage de la
 * carte : le serveur ne la met pas en cache, sauf une réponse incohérente, déjà facturée, qu'il
 * garde une heure et rend alors sans nouvel appel au fournisseur. Un `no_route` de lot peut venir
 * d'une liaison entre deux parcours : il ne prouve pas qu'un parcours seul est impraticable.
 */
export const routeTraceStaleTime = (route: LogisticsRoute | undefined): number =>
  route?.status === 'unavailable' &&
  (route.reason === 'provider_error' || route.reason === 'no_route')
    ? 0
    : Number.POSITIVE_INFINITY

/** Tracé reçu qui ne sera plus relu (`routeTraceStaleTime`). */
export const isFinalRouteTrace = (route: LogisticsRoute | undefined): boolean =>
  route !== undefined && routeTraceStaleTime(route) === Number.POSITIVE_INFINITY

/**
 * Le tracé du parcours courant est-il demandé (`settled` de `routeTraceStatusOf`) ? Une fois la
 * saisie posée (`debounced` : aucune modification pendant l'attente) et hors de l'édition d'une
 * étape (`editing`, champ GPS en cours de frappe : un point partiel lisible se facturerait à chaque
 * pause), ou sans attendre pour un tracé définitif déjà reçu (retour à un ordre précédent). Une
 * panne du fournisseur en cache, relue, attend comme un nouveau parcours : sinon chaque retour à
 * un ordre en panne relancerait un appel sans délai. Une requête en échec (`failed` : erreur HTTP,
 * sans donnée) se montre sans attendre, et sa requête reste coupée (`enabled` du hook).
 */
export const routeTraceSettledOf = ({
  key,
  debounced,
  editing,
  cached,
  failed,
}: {
  key: string | null
  debounced: boolean
  editing: boolean
  cached: LogisticsRoute | undefined
  failed: boolean
}): boolean => key !== null && (failed || isFinalRouteTrace(cached) || (debounced && !editing))
